import React, { useState, useRef, useEffect, useMemo } from "react";
import { httpClient } from '../../../HttpClient';
import { useEtatGlobal } from "../../EtatGlobal.js";
import "../Suivi.css";
import { HiOutlineMail } from "react-icons/hi";
import { IoIosSearch } from "react-icons/io";
import { CiFilter } from "react-icons/ci";
import { X } from "lucide-react";
import FicheClientOverlay from "../../Portefeuille/FicheClientOverlay";
import { missionCommencee, missionActive, estBloque, classeLigneBloquee } from "./suiviProdUtils";
import BadgeBloque from "./BadgeBloque";

import { dialog } from "../../../components/ui/Dialog";

// Un montant de déclaration négatif = crédit de TVA (l'État doit de l'argent
// au client) : il n'y a alors aucun paiement à effectuer, donc "Paiement
// effectué" devient sans objet et le mois est complet dès que la déclaration
// est faite. Demande du 21/09/2026 (ticket Suivi Prod_TVA).
// Le montant est stocké en texte formaté à la française ("-1 234,50 €"),
// d'où le nettoyage avant parseFloat. Un "-" seul (saisie en cours) donne
// NaN et n'est donc pas considéré comme un crédit.
const estCreditTVA = (montant) => {
  const num = parseFloat(
    String(montant || "")
      .replace(/\s/g, "")
      .replace(",", ".")
      .replace(/[^\d.-]/g, "")
  );
  return !isNaN(num) && num < 0;
};

function SuivitvaMensuel({ suivi, loading, refetch }) {
  const [selectedYear, setSelectedYear] = useState(null);
  const [showInvoiceForm, setShowInvoiceForm] = useState(false);
  const [initialFormData, setInitialFormData] = useState({});

  const [searchTerm, setSearchTerm] = useState("");
  const [filterRows, setFilterRows] = useState([
    { column: "collaborateur", selectedValues: [] },
  ]);
  const [showFilterOverlay, setShowFilterOverlay] = useState(false);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (overlayRef.current && !overlayRef.current.contains(event.target)) {
        setShowFilterOverlay(false);
      }
    };

    if (showFilterOverlay) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showFilterOverlay]);

  // 🔹 Extraire les valeurs uniques pour filtres
  const uniqueValues = useMemo(() => {
    const values = {
      formeSociale: Array.from(new Set(suivi.flatMap((c) => c.formeSociale))),
      regimeFiscal: Array.from(new Set(suivi.map((c) => c.regimeFiscal))),
      regimeTVA: Array.from(new Set(suivi.map((c) => c.regimeTVA))),
      expertComptable: Array.from(new Set(suivi.map((c) => c.expertComptable))),
      superviseur: Array.from(new Set(suivi.map((c) => c.superviseur))),
      collaborateur: Array.from(new Set(suivi.map((c) => c.collaborateur))),
      missionComptable: Array.from(
        new Set(suivi.map((c) => c.missionComptable))
      ),
      missionSociale: Array.from(new Set(suivi.map((c) => c.missionSociale))),
      missionJuridique: Array.from(
        new Set(suivi.map((c) => c.missionJuridique))
      ),
      missionIR: Array.from(new Set(suivi.map((c) => c.missionIR))),
      groupement: Array.from(new Set(suivi.map((c) => c.groupement))),
      statutDeclaration: Array.from(
        new Set(
          suivi.flatMap((c) => c.tvaMensuel.map((t) => t.statutDeclaration))
        )
      ),
      statutPaiement: Array.from(
        new Set(suivi.flatMap((c) => c.tvaMensuel.map((t) => t.statutPaiement)))
      ),
    };
    return values;
  }, [suivi]);

  // Récupère la liste des années disponibles pour le SELECT
  const getAvailableYears = (clients) => {
    if (!clients || clients.length === 0) return [];

    // Récupération de toutes les années présentes dans tvaMensuel
    let years = [];
    clients.forEach((client) => {
      const clotureYear = new Date(client.dateCloture).getFullYear();
      years.push(clotureYear);

      client.tvaMensuel.forEach((tva) => {
        years.push(tva.annees);
      });
    });

    // On garde unique et trié
    years = [...new Set(years)].sort((a, b) => a - b);

    // Ajout de l'année suivante
    const maxYear = Math.max(...years);
    years.push(maxYear + 1);

    return years;
  };

  const availableYears = getAvailableYears(suivi);

  // Si pas encore sélectionné, mettre la première année comme par défaut
  useEffect(() => {
    if (availableYears.length > 0 && !selectedYear) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  // Labels dynamiques avec année choisie
  const moisLabels = selectedYear
    ? Array.from({ length: 12 }, (_, i) => `${i + 1}/${selectedYear}`)
    : [];

  const [showDetailsOverlay, setShowDetailsOverlay] = useState(false);
  const [currentDetails, setCurrentDetails] = useState({});
  const [selectedClient, setSelectedClient] = useState({});

  // Fiche client partagée (mêmes états que SuiviJuridiqueAGO / SuiviBilan)
  const [ficheClientOpen, setFicheClientOpen] = useState(false);
  const [ficheClientData, setFicheClientData] = useState(null);
  const [ficheClientLoading, setFicheClientLoading] = useState(false);
  const ficheClientCache = useRef({});
  const [formData, setFormData] = useState({
    statutDeclaration: "",
    statutDeclarationMontant: "",
    statuDeclarationDate: null,
    statutPaiement: "",
    statutPaimentDate: null,
    commentaire: "",
  });

  const { utilisateur } = useEtatGlobal();

  // état initial
  const [invoiceData, setInvoiceData] = useState({
    fromEmail: "",
    toEmails: "",
    ccEmails: "",
    subject: "",
    message: "",
  });

  // quand on clique sur "Créer une facture"
  const toggleInvoiceForm = () => {
    const moisLabel = moisLabels[currentDetails.mois - 1] || "";
    const clientName = selectedClient.nom || "";
    const montant = formData.statutDeclarationMontant || "0";

    setInvoiceData({
      fromEmail: `${utilisateur.email}`,
      toEmails: `${selectedClient.email}`,
      ccEmails: "",
      subject: `TVA ${moisLabel}`,
      message: `  Bonjour,
  
  Nous vous informons que la TVA du mois de ${moisLabel} pour ${clientName} s’élève à ${montant} €.
  
  Aurions-nous votre accord pour procéder au télépaiement ?
  
  Restant dans l'attente de votre décision,
  
  Bien cordialement`, // <-- version texte
    });
    setShowInvoiceForm(!showInvoiceForm);
  };

  // soumission

  const [loadingEmailSend, setLoadingEmailSend] = useState(false);
  const handleInvoiceSubmit = async (e) => {
    e.preventDefault();
    setLoadingEmailSend(true);

    // Transformer le texte en HTML (chaque \n → <p>…</p>)
    const htmlMessage = invoiceData.message
      .split("\n")
      .map((line) => (line.trim() ? `<p>${line}</p>` : ""))
      .join("");

    try {
      await httpClient.post("https://backend-myalfa.vercel.app/api/email-reel", {
        fromEmail: invoiceData.fromEmail,
        toEmails: invoiceData.toEmails
          ? invoiceData.toEmails.split(",").map((email) => email.trim())
          : [],
        ccEmails: invoiceData.ccEmails
          ? invoiceData.ccEmails.split(",").map((email) => email.trim())
          : [],
        subject: invoiceData.subject,
        message: htmlMessage, // <-- version HTML
      });

      dialog.alert("Facture envoyée avec succès !");

      setInvoiceData({
        fromEmail: "",
        toEmails: "",
        ccEmails: "",
        subject: "",
        message: "",
      });

      // ✅ Fermer immédiatement après succès
      setLoadingEmailSend(false);
      setShowInvoiceForm(false);
      setShowDetailsOverlay(false);
      refetch();
    } catch (error) {
      console.error("Erreur lors de l’envoi de la facture :", error);
      dialog.alert("Impossible d’envoyer la facture.");
      setLoadingEmailSend(false);
      setShowInvoiceForm(false);
      setShowDetailsOverlay(false);
    }
  };

  const overlayRef = useRef(null);

  const showClientDetails = async (client) => {
    setFicheClientOpen(true);
    if (ficheClientCache.current[client.id]) {
      setFicheClientData(ficheClientCache.current[client.id]);
      setFicheClientLoading(false);
      return;
    }
    setFicheClientData(client);
    setFicheClientLoading(true);
    try {
      const { data } = await httpClient.get(
        `https://backend-myalfa.vercel.app/api/clients/${client.id}`
      );
      ficheClientCache.current[client.id] = data;
      setFicheClientData(data);
    } catch (err) {
      console.error("Erreur chargement client:", err);
    } finally {
      setFicheClientLoading(false);
    }
  };

  const handleCellClick = (client, mois) => {
    const record = client.tvaMensuel.find(
      (entry) => entry.mois === mois && entry.annees === selectedYear
    );

    const data = record
      ? {
          statutDeclaration: record.statutDeclaration || "",
          statutDeclarationMontant: record.statutDeclarationMontant || "",
          statuDeclarationDate: record.statuDeclarationDate || null,
          statutPaiement: record.statutPaiement || "",
          statutPaimentDate: record.statutPaimentDate || null,
          commentaire: record.commentaire || "",
        }
      : {
          statutDeclaration: "",
          statutDeclarationMontant: "",
          statuDeclarationDate: null,
          statutPaiement: "",
          statutPaimentDate: null,
          commentaire: "",
        };

    setFormData(data);
    setInitialFormData(data); // 🔹 sauvegarde initiale
    setCurrentDetails(record || { mois, annees: selectedYear });
    setSelectedClient(client);
    setShowDetailsOverlay(true);
  };

  const handleSubmit = async () => {
    try {
      await httpClient.put(
        `https://backend-myalfa.vercel.app/api/tvaMensuel/${currentDetails.annees}/${currentDetails.mois}/${selectedClient.id}`,
        formData
      );
    } catch (error) {
      console.error("Erreur lors de l'envoi du formulaire :", error);
      dialog.alert("Une erreur est survenue.");
    }
  };

  const formatDateTime = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <div>
        <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
          <div className="h-9 w-64 rounded-md bg-slate-200 animate-pulse" />
          <div className="flex items-center gap-5">
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
            <div className="h-9 w-20 rounded-md bg-slate-200 animate-pulse" />
          </div>
        </div>
        <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#6e1a36]">
                <th className="px-3 py-3.5 text-left">
                  <div className="h-3 rounded bg-[#ffffff33] animate-pulse w-24" />
                </th>
                {["Jan", "Fév", "Mar", "Avr", "Mai", "Jun", "Jul", "Aoû", "Sep", "Oct", "Nov", "Déc"].map((m, i) => (
                  <th key={i} className="px-2 py-3.5 text-center">
                    <div className="h-3 rounded bg-[#ffffff33] animate-pulse w-8 mx-auto" />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {Array.from({ length: 8 }).map((_, i) => (
                <tr key={i}>
                  <td className="px-3 py-3">
                    <div className="h-3 rounded bg-slate-200 animate-pulse w-32" />
                  </td>
                  {Array.from({ length: 12 }).map((__, j) => (
                    <td key={j} className="px-2 py-3 text-center">
                      <div className="h-2 rounded-md bg-slate-200 animate-pulse mx-auto w-8" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // 🔹 Gestion filtres dynamiques
  const handleAddFilter = () => {
    setFilterRows((prev) => [
      ...prev,
      { column: "collaborateur", selectedValues: [] },
    ]);
  };

  const handleRemoveFilter = (indexToRemove) => {
    setFilterRows((prev) => prev.filter((_, i) => i !== indexToRemove));
  };

  const handleFilterChange = (index, field, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) =>
        i === index ? { ...f, [field]: value, selectedValues: [] } : f
      )
    );
  };

  const handleCheckboxValueChange = (index, value) => {
    setFilterRows((prev) =>
      prev.map((f, i) => {
        if (i !== index) return f;
        const selectedValues = f.selectedValues || [];
        const updated = selectedValues.includes(value)
          ? selectedValues.filter((v) => v !== value)
          : [...selectedValues, value];
        return { ...f, selectedValues: updated };
      })
    );
  };

  const handleResetFilters = () => {
    setFilterRows([{ column: "collaborateur", selectedValues: [] }]);
    setSearchTerm("");
  };

  // 🔹 Appliquer les filtres et recherche
  const filteredData = suivi.filter((client) => {
    const matchesSearch = client.nom
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilters = filterRows.every((filter) => {
      if (!filter.selectedValues || filter.selectedValues.length === 0)
        return true;
      if (filter.column === "statutDeclaration") {
        return client.tvaMensuel.some((t) =>
          filter.selectedValues.includes(t.statutDeclaration)
        );
      }
      if (filter.column === "statutPaiement") {
        return client.tvaMensuel.some((t) =>
          filter.selectedValues.includes(t.statutPaiement)
        );
      }
      return filter.selectedValues.includes(client[filter.column]);
    });
    // Mission pas encore débutée cette année-là → masqué ; client archivé
    // depuis une année antérieure → masqué aussi.
    const missionDebutee =
      !selectedYear ||
      (missionCommencee(client, selectedYear) &&
        missionActive(client, selectedYear));
    return matchesSearch && matchesFilters && missionDebutee;
  });

  // Composant ProgressBar
  const ProgressBar = ({ record }) => {
    // Par défaut rien n'est complété
    let declaration = false;
    let paiement = false;

    if (record) {
      declaration = record.statutDeclaration === "oui";
      paiement = record.statutPaiement === "oui";
    }

    // Crédit de TVA : la déclaration seule vaut 100 %, le paiement ne compte
    // pas comme une étape manquante.
    const etapes = estCreditTVA(record?.statutDeclarationMontant)
      ? [declaration]
      : [declaration, paiement];
    const completed = etapes.filter(Boolean).length;
    const progressPercent = (completed / etapes.length) * 100;

    return (
      <div
        className={`w-4/5 h-2 mx-auto rounded-md border overflow-hidden transition-all duration-200 ${
          completed === 0
            ? "bg-slate-50 border-slate-300 hover:bg-slate-100"
            : "bg-slate-100 border-slate-200"
        }`}
      >
        <div
          className="h-full transition-all duration-300"
          style={{
            width: `${progressPercent}%`,
            backgroundColor: "green",
          }}
        />
      </div>
    );
  };

  const handleCloseOverlay = () => {
    setShowDetailsOverlay(false);
    const hasChanged =
      JSON.stringify(formData) !== JSON.stringify(initialFormData);
    if (hasChanged) {
      handleSubmit().then(() => refetch());
    }
  };

  const creditTVASaisi = estCreditTVA(formData.statutDeclarationMontant);

  return (
    <div>
      {/* Barre de recherche + filtres */}
      <div className="flex items-center justify-between gap-4 mb-5 mt-8 mr-2">
        <div className="relative flex-1 max-w-sm">
          <IoIosSearch className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Filtrer par nom du dossier..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-9 h-9 w-full rounded-md bg-slate-50 border border-slate-200 text-sm focus:bg-white focus:outline-none"
          />
        </div>
        <div className="flex items-center gap-5">
          <select
            value={selectedYear || ""}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
            className="border border-slate-300 rounded-md
                                    h-9 px-2 text-xs leading-tight w-20
                                    bg-white"
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
          <button
            onClick={() => setShowFilterOverlay(!showFilterOverlay)}
            className={`flex items-center gap-2 h-9 px-3 rounded-md font-semibold text-sm bg-white text-black border border-slate-300 hover:bg-slate-50`}
          >
            <CiFilter className="w-3 h-3" />
            Filtrer
          </button>
        </div>
        {showFilterOverlay && (
          <div
            ref={overlayRef}
            className="fixed top-[40%] left-[65%] -translate-x-1/2 bg-white border border-slate-200 rounded-lg p-4 shadow-lg z-50 w-2/5 max-h-[80vh] overflow-y-auto"
          >
            <div className="flex justify-between mb-2">
              <p className="font-semibold">Filtres avancés</p>
              <button
                className="text-sm text-slate-600 hover:text-slate-800"
                onClick={handleResetFilters}
              >
                Réinitialiser
              </button>
            </div>
            {filterRows.map((filter, index) => (
              <div key={index} className="mb-3">
                <div className="flex items-center justify-between mb-1">
                  <select
                    value={filter.column}
                    onChange={(e) =>
                      handleFilterChange(index, "column", e.target.value)
                    }
                    className="border border-slate-200 rounded-md p-1 text-sm"
                  >
                    {Object.keys(uniqueValues).map((col) => (
                      <option key={col} value={col}>
                        {col}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={() => handleRemoveFilter(index)}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    - Supprimer
                  </button>
                </div>
                <div className="flex flex-col ml-2">
                  {uniqueValues[filter.column]?.map((value) => (
                    <label key={value}>
                      <input
                        type="checkbox"
                        checked={filter.selectedValues.includes(value)}
                        onChange={() => handleCheckboxValueChange(index, value)}
                        className="mr-2"
                      />
                      {value || "(vide)"}
                    </label>
                  ))}
                </div>
              </div>
            ))}
            <button
              className="text-sm text-slate-700 hover:text-slate-900 mt-2"
              onClick={handleAddFilter}
            >
              + Ajouter filtre
            </button>
          </div>
        )}
      </div>

      {/* Tableau */}
      <div className="mt-6 bg-white rounded-xl border border-slate-100 shadow-sm overflow-hidden">
        <div
          className="overflow-y-auto"
          style={{ maxHeight: "calc(100vh - 250px)", minHeight: "420px" }}
        >
          <table
            className="w-full min-w-[1200px]"
            style={{ fontSize: "0.72rem" }}
          >
            <thead className="sticky top-0 z-10">
              <tr className="bg-[#6e1a36] text-xs">
                <th className="px-2 py-3.5 text-white font-semibold text-left">
                  Dossier
                </th>
                {moisLabels.map((m, i) => (
                  <th
                    className="px-2 py-3.5 text-white font-semibold text-center"
                    key={i}
                  >
                    {m}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredData.map((client) => (
                <tr
                  key={client.id}
                  className={`hover:bg-slate-50/50 transition-colors ${classeLigneBloquee(client)}`}
                >
                  <td
                    onClick={() => showClientDetails(client)}
                    className="px-2 py-2 font-medium text-slate-900 cursor-pointer hover:text-[#9B1B5D] transition-colors"
                  >
                    {client.nom}
                    {estBloque(client) && <BadgeBloque />}
                  </td>
                  {moisLabels.map((_, moisIndex) => {
                    const mois = moisIndex + 1;
                    const record = client.tvaMensuel.find(
                      (entry) =>
                        entry.mois === mois && entry.annees === selectedYear
                    );
                    return (
                      <td
                        key={mois}
                        className="px-2 py-1.5 text-center cursor-pointer"
                        onClick={() => handleCellClick(client, mois)}
                      >
                        <ProgressBar record={record} />
                      </td>
                    );
                  })}
                </tr>
              ))}
              {filteredData.length === 0 && (
                <tr>
                  <td
                    colSpan={13}
                    className="text-center py-12 text-sm text-slate-500"
                  >
                    Aucun client trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showDetailsOverlay && (
        <div className="overlayAC">
          <div className="inoverlayMS">
            <button
              aria-label="Fermer"
              onClick={handleCloseOverlay}
              className="absolute top-3 right-3 p-2 rounded-md text-gray-500 hover:text-gray-800 bg-gray-200 transition"
              style={{ zIndex: 10000 }}
            >
              <X className="w-4 h-4" />
            </button>

            <div className="w-[85%] mx-auto mt-5">
              <div className="border-b-4 border-[#8B1538] pb-4 mb-6">
                <h2 className="text-xl font-bold text-slate-900 m-0">
                  Détails TVA — {selectedClient.nom}
                </h2>
                <p className="text-xs text-slate-500 mt-1 m-0">
                  {moisLabels[currentDetails.mois - 1]}
                </p>
              </div>

              {/* Checkbox Déclaration */}
              <div className="flex items-center gap-4 mb-5">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={formData.statutDeclaration === "oui"}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        statutDeclaration: e.target.checked ? "oui" : "non",
                        statuDeclarationDate: e.target.checked
                          ? new Date()
                          : null,
                      })
                    }
                  />
                  Déclaration effectuée
                </label>
                <span className="text-xs text-slate-500">
                  {formatDateTime(formData.statuDeclarationDate)}
                </span>
              </div>

              <div className="flex items-center justify-between gap-4 mb-4">
                <div>
                  <label className="text-sm font-medium text-slate-700">
                    Montant déclaration :
                  </label>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Valeur négative acceptée (crédit de TVA)
                  </p>
                </div>
                <input
                  type="text"
                  name="statutDeclarationMontant"
                  placeholder="0 000,00"
                  value={formData.statutDeclarationMontant.replace(" €", "")}
                  onChange={(e) => {
                    // Le signe "-" (crédit de TVA) est retenu à part : le
                    // filtre ci-dessous le supprimerait à chaque frappe.
                    const negatif = e.target.value.trim().startsWith("-");
                    let raw = e.target.value
                      .replace(",", ".") // remplace virgule par point
                      .replace(/[^\d.]/g, ""); // garde seulement chiffres et point

                    if (raw === "") {
                      setFormData({
                        ...formData,
                        statutDeclarationMontant: negatif ? "-" : "",
                      });
                      return;
                    }

                    let num = parseFloat(raw);
                    if (!isNaN(num)) {
                      const parts = raw.split(".");
                      let formatted =
                        parts.length === 2
                          ? parts[0] + "," + parts[1].substring(0, 2)
                          : parts[0];

                      // Ajout des espaces pour milliers
                      formatted = formatted.replace(
                        /\B(?=(\d{3})+(?!\d))/g,
                        " "
                      );

                      setFormData({
                        ...formData,
                        statutDeclarationMontant:
                          (negatif ? "-" : "") + formatted,
                      });
                    }
                  }}
                  onBlur={() => {
                    const negatif = formData.statutDeclarationMontant
                      .trim()
                      .startsWith("-");
                    let input = formData.statutDeclarationMontant
                      .replace(",", ".")
                      .replace(/[^\d.]/g, "");
                    let num = parseFloat(input);

                    if (!isNaN(num)) {
                      let formatted = num
                        .toFixed(2) // 2 décimales
                        .replace(".", ",") // virgule
                        .replace(/\B(?=(\d{3})+(?!\d))/g, " "); // espace milliers

                      setFormData({
                        ...formData,
                        statutDeclarationMontant:
                          (negatif ? "-" : "") + formatted + " €",
                        // Crédit de TVA : on ne laisse pas un "Paiement
                        // effectué" coché hérité d'une saisie précédente.
                        ...(negatif && {
                          statutPaiement: "non",
                          statutPaimentDate: null,
                        }),
                      });
                    } else {
                      setFormData({
                        ...formData,
                        statutDeclarationMontant: "",
                      });
                    }
                  }}
                  className="w-1/2 h-10 rounded-md border-2 border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                />
              </div>

              <div className="w-full flex justify-end">
                <button
                  onClick={toggleInvoiceForm}
                  disabled={!formData.statutDeclarationMontant}
                  className={`px-4 py-1.5 rounded-md text-white transition ${
                    !formData.statutDeclarationMontant
                      ? "bg-slate-400 cursor-not-allowed"
                      : "bg-[#840040] hover:bg-[#6e1a36]"
                  }`}
                >
                  <HiOutlineMail size={20} />
                </button>
              </div>

              <hr className="my-6 border-slate-200" />
              <div className="flex items-center gap-4 mb-5">
                <label
                  className={`flex items-center gap-2 text-sm ${
                    creditTVASaisi ? "text-slate-400" : "text-slate-700"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={!creditTVASaisi && formData.statutPaiement === "oui"}
                    disabled={creditTVASaisi}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        statutPaiement: e.target.checked ? "oui" : "non",
                        statutPaimentDate: e.target.checked ? new Date() : null,
                      })
                    }
                  />
                  Paiement effectué
                </label>
                <span className="text-xs text-slate-500">
                  {creditTVASaisi
                    ? "Crédit de TVA — aucun paiement à effectuer"
                    : formatDateTime(formData.statutPaimentDate)}
                </span>
              </div>
              <div className="mb-5">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Commentaire
                </label>
                <textarea
                  className="w-full rounded-md border-2 border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538] transition-all"
                  value={formData.commentaire}
                  onChange={(e) =>
                    setFormData({ ...formData, commentaire: e.target.value })
                  }
                  rows={5}
                />
              </div>
              {showInvoiceForm && (
                <form
                  onSubmit={handleInvoiceSubmit}
                  className="mt-5 p-5 bg-slate-50 border border-slate-200 rounded-xl"
                >
                  <h3 className="text-sm font-bold text-[#9B1B5D] m-0 mb-4">
                    Envoyer une demande d'accord de paiement par mail
                  </h3>
                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      De
                    </label>
                    <input
                      type="email"
                      value={invoiceData.fromEmail}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          fromEmail: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Pour (séparés par virgule)
                    </label>
                    <input
                      type="text"
                      value={invoiceData.toEmails}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          toEmails: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      CC (séparés par virgule)
                    </label>
                    <input
                      type="text"
                      value={invoiceData.ccEmails}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          ccEmails: e.target.value,
                        })
                      }
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Objet
                    </label>
                    <input
                      type="text"
                      value={invoiceData.subject}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          subject: e.target.value,
                        })
                      }
                      required
                      className="w-full h-9 rounded-md border border-slate-300 px-3 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-xs font-medium text-slate-600 mb-1">
                      Message
                    </label>
                    <textarea
                      rows={10}
                      value={invoiceData.message}
                      onChange={(e) =>
                        setInvoiceData({
                          ...invoiceData,
                          message: e.target.value,
                        })
                      }
                      required
                      className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white outline-none focus:border-[#8B1538]"
                    />
                  </div>
                  <div className="flex justify-end">
                    <button
                      type="submit"
                      disabled={loadingEmailSend} // désactive le bouton pendant l'envoi
                      className={`px-6 h-10 rounded-md font-semibold text-sm text-white transition ${
                        loadingEmailSend
                          ? "bg-slate-400 cursor-not-allowed"
                          : "bg-emerald-600 hover:bg-emerald-700"
                      }`}
                    >
                      {loadingEmailSend
                        ? "En cours d'envoi..."
                        : "Envoyer le Mail"}
                    </button>
                  </div>
                </form>
              )}
              <br />
              <br />
            </div>
          </div>
        </div>
      )}

      <FicheClientOverlay
        client={ficheClientData}
        open={ficheClientOpen}
        loading={ficheClientLoading}
        initialTab="general"
        onClose={() => setFicheClientOpen(false)}
        onClientUpdated={(updated) => {
          ficheClientCache.current[updated.id] = updated;
          setFicheClientData(updated);
        }}
      />
    </div>
  );
}

export default SuivitvaMensuel;
